import { CheckIcon, CrossIcon } from '@vrplatform/icons';
import {
  showErrorNotification,
  updateNotification,
  showNotification,
} from '@vrplatform/theme';
import { showWarnNotification } from '@vrplatform/theme';
import { hasValue } from '@vrplatform/utils';
import { useNetwork } from '@mantine/hooks';
import {
  type InfiniteData,
  type QueryClient,
  type QueryObserverResult,
  type RefetchOptions,
  type RefetchQueryFilters,
  type QueryKey as TanstackQueryKey,
  useInfiniteQuery as useIQ,
  useMutation as useM,
  useQuery as useQ,
  useQueryClient,
} from '@tanstack/react-query';
import type { GeneratedSchema, Query } from '@vrplatform/graphql';
import type * as gqlV2 from '@vrplatform/graphql';
import type { GQtyError } from '@vrplatform/graphql';
import type React from 'react';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import {
  getRetryableReadErrorKind,
  getTransportErrorKind,
  retryHasuraQuery,
} from '../api/api.retry';
import type { QueryGroup } from '../api/api';
import {
  isClerkOfflineErrorMessage,
  isClerkSessionUnavailableErrorMessage,
} from '../api/api.utils';
import { GRAPHQL_URI, isStorybook, PLATFORM } from '../env';
import {
  captureSentryError,
  transportErrorCapturePolicy,
  type PollUntilCompletedProps,
  usePollUntilCompleted,
  useTeam,
  useSessionTokenProvider,
  useTeamIdContext,
} from '../hooks';
import { getTeamScopedPlaceholderData } from '../utils';
import {
  matchesApiQueryInvalidation,
  matchesApiQueryTenantScope,
  useShallowStableArray,
} from './react-query.utils';

export type { GQtyError, GeneratedSchema, Query, gqlV2 };

const iconColor = '#fff';
const iconSize = 18;

export type Client = ReturnType<typeof gqlV2.useHasuraClient>;

// The Hasura client (gqty schema, 1.3 MB) loads on first use so teams that never
// run a Hasura query never download it.
const context = createContext<() => Promise<Client>>(() =>
  Promise.reject(new Error('Hasura client provider is missing'))
);

const createLoadHasuraClient = (
  options: Parameters<typeof gqlV2.useHasuraClient>[0]
) => {
  let loading: Promise<Client> | undefined;

  return () => {
    loading ??= import('./hasura-client')
      .then((module) =>
        // biome-ignore lint/correctness/useHookAtTopLevel: its not a hook
        module.useHasuraClient(options)
      )
      .catch((error: unknown) => {
        // The next call loads the chunk again instead of failing for good.
        loading = undefined;
        throw error;
      });
    return loading;
  };
};

// const clientFetcher = (input: Request<unknown, CfProperties<unknown>>) => {
//   // let url = params.url;

//   // Stopped working with finalytic/graphql@2.0.85 since body is not a string anymore
//   // if (params.body && typeof params.body === 'string') {
//   //   const operation = (params.body as string).includes('"query":"mutation')
//   //     ? 'mutation'
//   //     : 'query';

//   //   // Remove duplicates and get all query keys
//   //   const queryKeys = [
//   //     ...new Set(
//   //       [...(params.body as string).matchAll(/{[a-zA-Z]+_/g)].map((i) =>
//   //         i[0].slice(1, -1)
//   //       )
//   //     ),
//   //   ]
//   //     .filter((i) => !['count'].includes(i))
//   //     .map((key) => `&queryKey=${key}`)
//   //     .join('');

//   //   url = `${url}?operation=${operation}${queryKeys}`;
//   // }

//   return fetch(input);
// };

export function ExtensionGtqyProvider({
  children,
  token,
}: {
  children: React.ReactNode;
  token: string;
}) {
  return (
    <DirectTokenGtqyProvider accessToken={token}>
      {children}
    </DirectTokenGtqyProvider>
  );
}

export function DirectTokenGtqyProvider({
  accessToken,
  children,
}: {
  accessToken: string;
  children: React.ReactNode;
}) {
  const ref = useRef<(() => Promise<Client>) | null>(null);
  if (!ref.current) {
    function getTokenHeaders() {
      const headers: { [s: string]: string } = {
        Authorization: `Bearer ${accessToken}`,
        'X-Transaction-ID': Math.random().toString(36).substring(2, 9),
        'X-Verification-Key': '5db60e10-fe6f-4726-9128-d2708285892e',
      };
      if (PLATFORM) headers['Finalytic-Platform'] = PLATFORM;
      return headers;
    }

    ref.current = createLoadHasuraClient({
      headers: () => getTokenHeaders(),
      subscriptions: true,
      attempts: 1,
      credentials: 'include',
    });
  }

  return createElement(context.Provider, { value: ref.current }, children);
}
export function GtqyClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getToken } = useSessionTokenProvider();

  const client = useMemo(() => {
    async function getTokenHeaders() {
      const accessToken = await getToken();
      const headers: { [s: string]: string } = {
        Authorization: `Bearer ${accessToken}`,
        'X-Transaction-ID': Math.random().toString(36).substring(2, 9),
        'X-Verification-Key': '5db60e10-fe6f-4726-9128-d2708285892e',
      };
      if (PLATFORM) headers['Finalytic-Platform'] = PLATFORM;
      return headers;
    }

    return createLoadHasuraClient({
      headers: async () => await getTokenHeaders(),
      subscriptions: true,
      attempts: 1,
      credentials: 'include',
    });
  }, [getToken]);

  return createElement(context.Provider, { value: client }, children);
}

export type V2MutationOptions<TData, _TArgs> = {
  onError?: (error: any) => void;
  onCompleted?: (data: TData) => void;
  invalidateQueryKeys?: (QueryKeyUnion | (string & {}))[];
  invalidateExact?: boolean;
  storybookQueryKey?: string;
};

function useMutationBasic<TData, TArgs>(
  fn: (mutation: gqlV2.Mutation, vars: TArgs) => TData,
  options?: V2MutationOptions<TData, TArgs>
) {
  const loadClient = useContext(context);

  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
    data,
  } = useM({
    mutationFn: async (mutationOptions: { args: TArgs }) => {
      if (isStorybook && options?.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          {
            method: 'POST',
          }
        ).then((res) => res.json() as Promise<TData>);
      }

      const { client } = await loadClient();

      return await client.resolved<TData>(
        () => fn(client.mutation, mutationOptions.args),
        {
          noCache: true,
        }
      );
    },
    onSuccess: options?.onCompleted,
    onError: options?.onError,
    onSettled: () => {
      if (options?.invalidateQueryKeys) {
        options.invalidateQueryKeys.forEach((key) => {
          queryClient.invalidateQueries({
            queryKey: [key],
            exact: options?.invalidateExact,
          });
        });
        queryClient.invalidateQueries({ queryKey: ['default'] });
      }
    },
  });
  return [mutateAsync, { isLoading, error, data }] as const;
}

type HasuraInvalidateKey = QueryKeyUnion | (string & {});

const mergeInvalidateKeys = <T extends string>(
  initialKeys: readonly T[] | undefined,
  keys: readonly T[] | T | undefined
) => {
  const merged: T[] = [];

  if (Array.isArray(initialKeys)) merged.push(...initialKeys);
  if (Array.isArray(keys)) merged.push(...keys);
  if (typeof keys === 'string') merged.push(keys);

  return merged;
};

const uniqueInvalidateKeys = <T extends string>(keys: readonly T[]) => [
  ...new Set(keys),
];

type ApiInvalidateQueryFilters = {
  groups?: readonly QueryGroup[] | QueryGroup;
  includeUniqueQueryKeys?: readonly string[] | string;
  excludeUniqueQueryKeys?: readonly string[] | string;
};

type ApiInvalidateInput =
  | readonly QueryGroup[]
  | QueryGroup
  | ApiInvalidateQueryFilters;

const isApiInvalidateQueryFilters = (
  value: ApiInvalidateInput | undefined
): value is ApiInvalidateQueryFilters =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export function useInvalidateHasuraQueries(
  invalidateQueryKeys?: readonly HasuraInvalidateKey[]
) {
  const queryClient = useQueryClient();
  const stableInvalidateQueryKeys = useShallowStableArray(invalidateQueryKeys);

  return useCallback(
    (keys?: readonly HasuraInvalidateKey[] | HasuraInvalidateKey) => {
      const toInvalidate = mergeInvalidateKeys(stableInvalidateQueryKeys, keys);

      if (!toInvalidate.length) return;

      toInvalidate.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key], type: 'all' });
      });
      queryClient.invalidateQueries({ queryKey: ['default'] });
    },
    [queryClient, stableInvalidateQueryKeys]
  );
}

export function useInvalidateApiQueries(
  invalidateQueryGroups?: ApiInvalidateInput
) {
  const queryClient = useQueryClient();
  const { teamId } = useTeamIdContext();
  const stableInvalidateQueryGroups = useShallowStableArray(
    uniqueInvalidateKeys(
      mergeInvalidateKeys(
        undefined,
        isApiInvalidateQueryFilters(invalidateQueryGroups)
          ? invalidateQueryGroups.groups
          : invalidateQueryGroups
      )
    )
  );
  const stableIncludeUniqueQueryKeys = useShallowStableArray(
    uniqueInvalidateKeys(
      mergeInvalidateKeys(
        undefined,
        isApiInvalidateQueryFilters(invalidateQueryGroups)
          ? invalidateQueryGroups.includeUniqueQueryKeys
          : undefined
      )
    )
  );
  const stableExcludeUniqueQueryKeys = useShallowStableArray(
    uniqueInvalidateKeys(
      mergeInvalidateKeys(
        undefined,
        isApiInvalidateQueryFilters(invalidateQueryGroups)
          ? invalidateQueryGroups.excludeUniqueQueryKeys
          : undefined
      )
    )
  );

  return useCallback(
    (groups?: ApiInvalidateInput) => {
      const invalidateQueryGroups = uniqueInvalidateKeys(
        mergeInvalidateKeys(
          stableInvalidateQueryGroups,
          isApiInvalidateQueryFilters(groups) ? groups.groups : groups
        )
      );
      const includeUniqueQueryKeys = uniqueInvalidateKeys(
        mergeInvalidateKeys(
          stableIncludeUniqueQueryKeys,
          isApiInvalidateQueryFilters(groups)
            ? groups.includeUniqueQueryKeys
            : undefined
        )
      );
      const excludeUniqueQueryKeys = uniqueInvalidateKeys(
        mergeInvalidateKeys(
          stableExcludeUniqueQueryKeys,
          isApiInvalidateQueryFilters(groups)
            ? groups.excludeUniqueQueryKeys
            : undefined
        )
      );
      const toInvalidate = mergeInvalidateKeys(
        undefined,
        invalidateQueryGroups
      );

      // Resolves when the refetches settle; failed refetches do not reject.
      return Promise.all(
        toInvalidate.map((queryGroup) =>
          queryClient.invalidateQueries({
            queryKey: [queryGroup],
            type: 'all',
            predicate: ({ meta, queryKey }) =>
              matchesApiQueryTenantScope(meta, teamId) &&
              matchesApiQueryInvalidation(queryKey, {
                includeUniqueQueryKeys,
                excludeUniqueQueryKeys,
              }),
          })
        )
      );
    },
    [
      queryClient,
      stableExcludeUniqueQueryKeys,
      stableIncludeUniqueQueryKeys,
      stableInvalidateQueryGroups,
      teamId,
    ]
  );
}

export const useMutation = <TData, TArgs>(
  fn: (mutation: gqlV2.Mutation, vars: TArgs) => TData,
  options?: V2MutationOptions<TData, TArgs> & {
    successMessage?: {
      id?: string;
      message?: string;
      title?: string;
    };
    errorMessage?: {
      title?: string | ((err: Error) => string | undefined);
      message?: string | ((err: Error) => string | undefined);
    };
    hideNotification?: boolean;
  }
) => {
  const notifyRef = useId();

  const notifyId = options?.successMessage?.id || notifyRef;

  const [mutate, { isLoading, error }] = useMutationBasic(fn, {
    ...options,
    invalidateExact: options?.invalidateExact,
    invalidateQueryKeys: options?.invalidateQueryKeys,
    onCompleted: (data) => {
      if (options?.onCompleted) options.onCompleted(data);

      if (options?.hideNotification) return;

      if (options?.successMessage) {
        updateNotification({
          id: notifyId,
          message:
            options?.successMessage?.message ||
            'Sucessfully updated your action.',
          title: options?.successMessage?.title || 'Success!',
          color: 'teal',
          icon: <CheckIcon color={iconColor} size={iconSize} />,
          radius: 10,
          loading: false,
          autoClose: 3000,
        });
      }
    },
    onError: (error) => {
      const getTitle = (
        type: 'title' | 'message',
        defaultValue: string | undefined
      ) => {
        if (typeof options?.errorMessage?.[type] === 'function')
          return (options.errorMessage?.[type] as any)?.(error) || defaultValue;
        return options?.errorMessage?.[type] || defaultValue;
      };

      const title = getTitle('title', error.name);
      const message = getTitle('message', error.message);

      if (options?.hideNotification) return;

      if (options?.successMessage) {
        updateNotification({
          id: notifyId,
          title,
          message,
          color: 'red',
          icon: <CrossIcon color={iconColor} size={iconSize} />,
          radius: 10,
          loading: false,
          autoClose: 3000,
        });
      } else {
        showErrorNotification({
          title,
          message,
        });
      }
    },
  });

  const m: typeof mutate = async (...opts) => {
    if (options?.successMessage && !options?.hideNotification) {
      showNotification({
        id: notifyId,
        loading: true,
        title: 'Loading...',
        message: 'We will update you shortly.',
        autoClose: false,
        radius: 10,
      });
    }

    const result = await mutate(...opts);
    return result;
  };

  return { mutate: m, loading: isLoading, error };
};

export { useQueryClient };

export const useSubscription = <TData, TVariables = undefined>(
  fn: (sub: gqlV2.Subscription, variables: TVariables) => TData,
  variables?: TVariables,
  options?: {
    skip?: boolean;
    queryKey?: QueryKeyUnion | QueryKeyUnion[];
    keepPreviousData?: boolean;
    onSubscriptionData?: (data: TData) => void;
  }
): {
  data: TData | undefined;
  error?: any;
  isLoading: boolean;
  isFetching: boolean;
  isInitialLoading: boolean;
  isCalled: boolean;
  refetch: (
    options?: (RefetchOptions & RefetchQueryFilters) | undefined
  ) => Promise<QueryObserverResult<TData, Error>>;
} => {
  const errorRef = useRef<any>(undefined);
  const loadClient = useContext(context);
  const uniqueKey = useRef('');
  const [team, , isFetchingTeam] = useTeam();
  const teamId = team?.id;
  const networkStatus = useNetwork();

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const baseQueryKey: any[] = [
    options?.queryKey,
    uniqueKey.current,
    variables,
  ].filter(hasValue);
  const queryKey = [...baseQueryKey, teamId];

  const queryClient = useQueryClient();

  const skip = (!!options?.skip || isFetchingTeam) && !!networkStatus.online;

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    loadClient()
      .then(({ subscribe }) => {
        if (cancelled) return;

        unsubscribe = subscribe(
          (s) => fn(s, variables!),
          (data) => {
            errorRef.current = undefined;

            // Tried fixing blib, but not luck
            // const skip =
            //   Array.isArray(data) &&
            //   data.length &&
            //   'id' in data[0] &&
            //   data.every((x) => !x.id);
            // if (skip) return;

            options?.onSubscriptionData?.(data);
            queryClient.setQueryData(queryKey, () => data);
          },
          (error) => {
            errorRef.current = error;
            // queryClient.setQueryData(queryKey, undefined);
          }
        );
      })
      .catch((error: unknown) => {
        if (!cancelled) errorRef.current = error;
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [
    options?.onSubscriptionData,
    fn,
    queryClient.setQueryData,
    queryKey,
    loadClient,
    variables,
  ]);

  const { isLoading, data, error, refetch, isFetching, isInitialLoading } =
    useQ<TData, Error>({
      queryKey,
      queryFn: async () => {
        const { client } = await loadClient();

        return client.resolved(() => fn(client.query as any, variables!), {});
      },
      enabled: !skip,
      placeholderData: options?.keepPreviousData
        ? getTeamScopedPlaceholderData(teamId)
        : undefined,
    });

  return {
    isLoading,
    data: data as any,
    error: errorRef.current || error,
    isCalled: true,
    refetch,
    isFetching,
    isInitialLoading,
  };
};

export type QueryKeyUnion =
  | 'flows'
  | 'emailTemplates'
  | 'taxStatements'
  | 'ownerStatements'
  | 'ownerStatementTemplates'
  | 'ownerStatementTemplateCollections'
  | 'settings'
  | 'automations'
  | 'automationTemplates'
  | 'bookingChannels'
  | 'apps'
  | 'connections'
  | 'tenantUsers'
  | 'teams'
  | 'users'
  | 'sources'
  | 'paymentLineClassifications'
  | 'paymentLines'
  | 'listings'
  | 'listingCollections'
  | 'listingOwners'
  | 'listingConnections'
  | 'reservations'
  | 'payments'
  | 'tasks'
  | 'auditLogs'
  | 'files'
  | 'scheduledEvents'
  | 'owners'
  | 'features'
  | 'featureApprovals'
  | 'featureEnabledTeams'
  | 'jobPlans'
  | 'actions'
  | 'token'
  | 'views'
  | 'issues'
  | 'activity'
  | 'accounts'
  | 'expenses'
  | 'deposits'
  | 'journalEntries'
  | 'customFees'
  | 'issueMessageOverwrites'
  | 'taxRates'
  | 'bankRecords'
  | 'recurringExpenses';

class HasuraQueryError extends Error {
  constructor(message: string, cause: unknown) {
    super(message, { cause });
    this.name = 'ReactQueryError';
  }
}

export type QueryKey =
  | (QueryKeyUnion | (string & {})) // allows autocomplete for union but also pass any string
  | (QueryKeyUnion | (string & {}))[];

type HasuraQueryKind = 'query' | 'infinite-query';

/**
 * TanStack decides whether to retry only after the query function rejects.
 * `fetchFailureCount` is the value it will pass to `retry`, so the same policy
 * tells us whether this attempt is terminal before we notify or report it.
 */
const getTerminalQueryAttemptCount = (
  queryClient: QueryClient,
  queryKey: TanstackQueryKey,
  error: unknown
) => {
  const failureCount =
    queryClient.getQueryState(queryKey)?.fetchFailureCount ?? 0;

  return retryHasuraQuery(failureCount, error) ? undefined : failureCount + 1;
};

function handleQueryError(
  error: unknown,
  signal: AbortSignal,
  extra: {
    attemptCount: number;
    isOnline: boolean;
    query: string;
    queryKind: HasuraQueryKind;
  }
) {
  if (window?.location?.host === 'localhost:4009') return;

  const title = 'Query Error';
  const message = error instanceof Error ? error.message : undefined;

  // hide error on speciific issues
  if (
    signal?.aborted ||
    !message ||
    message === `Unexpected token '<', "<!DOCTYPE "... is not valid JSON` ||
    message ===
      'Could not verify JWT: JWSError (CompactDecodeError Invalid number of parts: Expected 3 parts; got 1)'
  )
    return;

  const lower = message.toLowerCase();
  const isClerkOfflineError = isClerkOfflineErrorMessage(message);
  const isClerkSessionUnavailableError =
    isClerkSessionUnavailableErrorMessage(message);
  const transportErrorKind = getTransportErrorKind(error);
  const isTransportError = transportErrorKind !== undefined;
  const isConnectionError =
    getRetryableReadErrorKind(error) === 'connection-error';
  const hasuraHost = new URL(GRAPHQL_URI).host;
  const transportCapture = transportErrorKind
    ? transportErrorCapturePolicy.evaluate({
        errorKind: transportErrorKind,
        host: hasuraHost,
        isOnline: extra.isOnline,
      })
    : undefined;
  const connectionCapture = isConnectionError
    ? transportErrorCapturePolicy.evaluate({
        errorKind: 'backend-connection',
        host: hasuraHost,
        isOnline: extra.isOnline,
      })
    : undefined;

  const disableNotification =
    isTransportError ||
    isConnectionError ||
    lower.includes('jwtexpired') ||
    lower.includes('database query error') ||
    isClerkOfflineError ||
    isClerkSessionUnavailableError;

  const disableSentry =
    lower.includes('jwtexpired') ||
    lower.includes('signal is aborted without reason') ||
    isClerkOfflineError ||
    transportCapture?.shouldCapture === false ||
    connectionCapture?.shouldCapture === false ||
    isClerkSessionUnavailableError;

  if (!disableSentry)
    captureSentryError(new HasuraQueryError(message, error), {
      // PRO-17532/PRO-18481: exhausted transport and backend connection
      // retries are expected infrastructure signals, not application errors.
      level: isTransportError || isConnectionError ? 'warning' : 'error',
      fingerprint: isTransportError
        ? ['hasura-query-transport-terminal']
        : isConnectionError
          ? ['hasura-query-connection-terminal']
          : ['{{ default }}', 'hasura-query', message],
      tags: isTransportError
        ? {
            error_kind: 'transport',
            error_source: 'hasura-query',
            hasura_host: hasuraHost,
            query_kind: extra.queryKind,
            transport_error_kind: transportErrorKind,
          }
        : isConnectionError
          ? {
              error_kind: 'backend-connection',
              error_source: 'hasura-query',
              hasura_host: hasuraHost,
              query_kind: extra.queryKind,
            }
          : undefined,
      extra: {
        ...extra,
        ...(transportCapture
          ? {
              suppressed_transport_error_count:
                transportCapture.suppressedCount,
              transport_error_window_ms: transportCapture.windowMs,
            }
          : {}),
        ...(connectionCapture
          ? {
              suppressed_connection_error_count:
                connectionCapture.suppressedCount,
              connection_error_window_ms: connectionCapture.windowMs,
            }
          : {}),
      },
    });

  if (disableNotification) return;

  // TODO: this is only for now - will add this back in later
  if (lower.includes('not found in type')) {
    return;
    // title = 'Query Permission Error';
    // message = 'Missing user permissions on query.';
  }

  // ignore showing error message for weird undefined ID error when refocusing window after long time
  if (message.includes(`expecting a value for non-nullable variable: "id1"`))
    return;

  if (
    message.includes(
      'The operation exceeded the time limit of 60s allowed for this project'
    )
  ) {
    return showWarnNotification({
      title: 'Connection Timeout',
      message:
        'Due to a possible slow internet connection, or temporarily high load on our servers, the connection from your computer has timed out. Please try again later.',
    });
  }

  showErrorNotification({
    title,
    message,
  });
}

type BaseQueryOptions<TVariables> = {
  variables?: TVariables;
  skip?: boolean;
  queryKey?: QueryKey;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  refetchOnMount?: boolean;
  gcTime?: number;
  storybookQueryKey?: string;
  teamScoped?: boolean;
};

export const useQuery = <TData, TVariables = undefined>(
  fn: (query: GeneratedSchema['query'], variables: TVariables) => TData,
  options?: BaseQueryOptions<TVariables> & {
    keepPreviousData?: boolean;
    staleTime?: number;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted'
    > & {
      isCompleted: (data: TData | undefined) => boolean;
    };
  }
) => {
  const uniqueKey = useRef('');
  const networkStatus = useNetwork();
  const [team, , isFetchingTeam] = useTeam();
  const isTeamScoped = options?.teamScoped ?? true;
  const teamId = isTeamScoped ? team?.id : undefined;

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const loadClient = useContext(context);
  const variables = options?.variables || ({} as TVariables);
  const skip: boolean =
    (!!options?.skip || (isTeamScoped && isFetchingTeam)) &&
    !!networkStatus.online;

  const queryKeys = Array.isArray(options?.queryKey)
    ? options?.queryKey || []
    : options?.queryKey
      ? [options.queryKey]
      : [];

  const qk: TanstackQueryKey = [
    ...queryKeys,
    'global',
    uniqueKey.current,
    variables,
    networkStatus.online,
    ...(isTeamScoped ? [teamId] : []),
  ];

  const { isFetching, isPaused, data, error, refetch, isInitialLoading } = useQ<
    TData,
    Error
  >({
    enabled: !skip,
    queryKey: qk,
    retry: retryHasuraQuery,
    queryFn: async ({ client: queryClient, signal }) => {
      if (isStorybook && options?.storybookQueryKey) {
        return await fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          { signal }
        ).then((res) => res.json() as Promise<TData>);
      }

      return loadClient()
        .then(({ client }) =>
          client.resolved(() => fn(client.query, variables), {
            fetchOptions: {
              signal,
            },
          })
        )
        .catch((err: unknown) => {
          const attemptCount = getTerminalQueryAttemptCount(
            queryClient,
            qk,
            err
          );

          if (attemptCount !== undefined) {
            handleQueryError(err, signal, {
              attemptCount,
              query: uniqueKey.current,
              queryKind: 'query',
              isOnline: networkStatus.online,
            });
          }
          throw err;
        });
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus || false,
    refetchOnReconnect: options?.refetchOnReconnect,
    refetchOnMount: options?.refetchOnMount,
    placeholderData: options?.keepPreviousData
      ? getTeamScopedPlaceholderData(teamId)
      : undefined,
    staleTime:
      options?.staleTime ?? (options?.keepPreviousData ? 30_000 : undefined),
    gcTime: options?.gcTime,
    meta: {
      teamId: isTeamScoped ? teamId : undefined,
      teamScoped: isTeamScoped,
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(data);
  }, [data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip: skip || isFetching || isPaused || Boolean(error),
          interval: options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: refetch,
        }
      : undefined
  );

  return {
    data: data,
    error,
    refetch,
    isLoading: isFetching,
    isInitialLoading,
    isFetching,
    isCalled: true,
    queryKey: qk,
  };
};

type InfiniteQueryParams = {
  limit: number;
  offset: number;
};

export const useInfiniteQuery = <
  TRow,
  TData extends { aggregate: number; list: TRow[] },
  TVariables = undefined,
>(
  fn: (
    query: GeneratedSchema['query'],
    variables: TVariables,
    queryParams: InfiniteQueryParams
  ) => TData,
  options?: BaseQueryOptions<TVariables> & {
    pageLimit?: number;
    poll?: Omit<
      PollUntilCompletedProps,
      'refetchQuery' | 'skip' | 'isCompleted'
    > & {
      isCompleted: (data: InfiniteData<TData> | undefined) => boolean;
    };
  }
) => {
  const uniqueKey = useRef('');
  const networkStatus = useNetwork();
  const [team, , isFetchingTeam] = useTeam();
  const isTeamScoped = options?.teamScoped ?? true;
  const teamId = isTeamScoped ? team?.id : undefined;

  if (!uniqueKey.current) uniqueKey.current = fn.toString();

  const loadClient = useContext(context);
  const variables = options?.variables || ({} as TVariables);
  const skip: boolean =
    (!!options?.skip || (isTeamScoped && isFetchingTeam)) &&
    !!networkStatus.online;

  const queryKeys = Array.isArray(options?.queryKey)
    ? options?.queryKey || []
    : options?.queryKey
      ? [options.queryKey]
      : [];

  const queryKey = [
    ...queryKeys,
    'global',
    uniqueKey.current,
    variables,
    ...(isTeamScoped ? [teamId] : []),
  ];

  const pageLimit = options?.pageLimit || 50;

  const {
    isFetching,
    isPaused,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  } = useIQ<TData, Error>({
    queryKey,
    retry: retryHasuraQuery,
    queryFn: async (params) => {
      if (isStorybook && options?.storybookQueryKey) {
        return fetch(
          `${window.location.origin}/storybook/${options.storybookQueryKey}`,
          {
            signal: params.signal,
          }
        ).then((res) => res.json() as Promise<TData>);
      }

      return await loadClient()
        .then(({ client }) =>
          client.resolved(
            () =>
              fn(client.query as any, variables, {
                limit: pageLimit,
                offset:
                  (typeof params.pageParam === 'number'
                    ? params.pageParam
                    : 0) * pageLimit,
              }),
            {
              fetchOptions: {
                signal: params.signal,
              },
            }
          )
        )
        .catch((err: unknown) => {
          const attemptCount = getTerminalQueryAttemptCount(
            params.client,
            queryKey,
            err
          );

          if (attemptCount !== undefined) {
            handleQueryError(err, params.signal, {
              attemptCount,
              query: uniqueKey.current,
              queryKind: 'infinite-query',
              isOnline: networkStatus.online,
            });
          }
          throw err;
        });
    },
    enabled: !skip,
    getNextPageParam: (lastPage, pages) => {
      const hasNext = lastPage.aggregate > pages.length * pageLimit;
      return hasNext ? pages.length : null; // return null if no more pages
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus || false,
    placeholderData: isTeamScoped
      ? getTeamScopedPlaceholderData(teamId)
      : undefined,
    initialPageParam: 0,
    meta: {
      teamId: isTeamScoped ? teamId : undefined,
      teamScoped: isTeamScoped,
    },
  });

  const isCompleted = useMemo(() => {
    return !!options?.poll?.isCompleted(data);
  }, [data, options?.poll?.isCompleted]);

  usePollUntilCompleted(
    options?.poll
      ? {
          isCompleted,
          skip: skip || isFetching || isPaused || Boolean(error),
          interval: options.poll.interval,
          onCompleted: options.poll.onCompleted,
          refetchQuery: refetch,
        }
      : undefined
  );

  return {
    data,
    error,
    refetch,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
  };
};

export function useGqtyClient() {
  return useContext(context);
}
